import React, { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BookingFlow from '../components/BookingFlow';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url("/images/hero-bg.png")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.common.white,
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
  },
}));

const Feature = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.light,
  transition: 'transform 0.2s ease-in-out',
  border: 'none',
  '&:hover': {
    transform: 'translateY(-8px)',
    backgroundColor: theme.palette.background.paper,
  },
}));

const features = [
  {
    icon: <DirectionsCarIcon sx={{ fontSize: 48 }} />,
    title: 'Mercedes V-Class Fleet',
    description: 'Experience ultimate comfort with our exclusive fleet of luxury Mercedes V-Class vehicles.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 48 }} />,
    title: 'Professional Chauffeurs',
    description: 'Our highly trained chauffeurs provide a discreet, reliable, and professional service.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 48 }} />,
    title: 'Dedicated Service',
    description: 'Personal attention to every detail of your journey, available 24/7.',
  },
];

const testimonialImages = [
  '/images/testimonials/1.png',
  '/images/testimonials/2.png',
  '/images/testimonials/3.png',
  '/images/testimonials/4.png'
];

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const bookingWidgetRef = useRef(null);

  const handleBookingClick = () => {
    if (isMobile && bookingWidgetRef.current) {
      bookingWidgetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      window.location.href = '/booking';
    }
  };

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={6}>
              <Box py={{ xs: 4, md: 8 }}>
                <Typography 
                  variant="h1" 
                  component="h1" 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.5rem' },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    lineHeight: { xs: 1.2, md: 1.1 },
                    mb: { xs: 2, md: 3 },
                  }}
                >
                  Eminent Chauffeurs
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: { xs: 1, md: 2 },
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                    opacity: 0.95,
                  }}
                >
                  Every Journey is a Statement
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: { xs: 3, md: 4 },
                    maxWidth: 600,
                    mx: { xs: 'auto', md: 0 },
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                    lineHeight: { xs: 1.5, md: 1.6 },
                  }}
                >
                  Experience unparalleled luxury with our exclusive Mercedes V-Class chauffeur service in the UK
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Button
                    onClick={handleBookingClick}
                    variant="contained"
                    size="large"
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: 'common.white',
                      color: 'background.dark',
                      '&:hover': {
                        bgcolor: 'common.white',
                        opacity: 0.9,
                      },
                      minWidth: { xs: '100%', sm: 200 },
                    }}
                  >
                    Book Your Journey
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/services"
                    variant="outlined"
                    size="large"
                    fullWidth={isMobile}
                    sx={{
                      borderColor: 'common.white',
                      color: 'common.white',
                      '&:hover': {
                        borderColor: 'common.white',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                      minWidth: { xs: '100%', sm: 200 },
                    }}
                  >
                    Our Services
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                ref={bookingWidgetRef}
                sx={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 2,
                  p: { xs: 2, md: 3 },
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  mt: { xs: 2, md: 0 },
                  scrollMarginTop: { xs: '64px', md: '72px' },
                }}
              >
                <BookingFlow variant="home" />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Trusted By Strip */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          py: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="subtitle2"
            component="p" 
            textAlign="center" 
            color="text.secondary"
            sx={{ 
              mb: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              textTransform: 'uppercase',
              letterSpacing: '1px',
              opacity: 0.8,
            }}
          >
            Trusted By
          </Typography>
          <Grid 
            container 
            spacing={2}
            justifyContent="center" 
            alignItems="center"
          >
            <Grid item xs={4} sm={3} md={2}>
              <Box
                component="img"
                src="/images/brands/mobo-awards.png"
                alt="MOBO Awards"
                sx={{
                  width: '100%',
                  maxWidth: 120,
                  height: 'auto',
                  filter: 'grayscale(100%)',
                  opacity: 0.7,
                  transition: 'all 0.3s ease',
                  mx: 'auto',
                  display: 'block',
                  '&:hover': {
                    filter: 'grayscale(0%)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={4} sm={3} md={2}>
              <Box
                component="img"
                src="/images/brands/sony.png"
                alt="Sony"
                sx={{
                  width: '100%',
                  maxWidth: 120,
                  height: 'auto',
                  filter: 'grayscale(100%)',
                  opacity: 0.7,
                  transition: 'all 0.3s ease',
                  mx: 'auto',
                  display: 'block',
                  '&:hover': {
                    filter: 'grayscale(0%)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={4} sm={3} md={2}>
              <Box
                component="img"
                src="/images/brands/snap-fitness.png"
                alt="Snap Fitness"
                sx={{
                  width: '100%',
                  maxWidth: 120,
                  height: 'auto',
                  filter: 'grayscale(100%)',
                  opacity: 0.7,
                  transition: 'all 0.3s ease',
                  mx: 'auto',
                  display: 'block',
                  '&:hover': {
                    filter: 'grayscale(0%)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 12 } }}>
        <Typography 
          variant="h2" 
          component="h2" 
          textAlign="center" 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.5rem' },
            fontWeight: 700,
            letterSpacing: '-0.02em',
            mb: { xs: 1.5, md: 3 },
            lineHeight: { xs: 1.2, md: 1.1 },
          }}
        >
          The Eminent Experience
        </Typography>
        <Typography 
          variant="h6" 
          textAlign="center" 
          color="text.secondary" 
          sx={{ 
            mb: { xs: 3, md: 8 },
            maxWidth: 600,
            mx: 'auto',
            fontWeight: 400,
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
            lineHeight: { xs: 1.5, md: 1.6 },
            opacity: 0.85,
          }}
        >
          Setting new standards in luxury chauffeur services
        </Typography>
        
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Feature>
                <Box sx={{ color: 'primary.main', mb: { xs: 1, md: 2 } }}>
                  {feature.icon}
                </Box>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  {feature.description}
                </Typography>
              </Feature>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Add Testimonials Section */}
      <Box sx={{ bgcolor: 'background.dark', py: { xs: 6, md: 12 }, color: 'common.white' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            component="h2" 
            textAlign="center" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mb: { xs: 4, md: 6 },
            }}
          >
            Client Testimonials
          </Typography>
          
          <Box sx={{ 
            '.slick-dots': {
              bottom: '-40px',
              '& li button:before': {
                color: 'white',
              },
              '& li.slick-active button:before': {
                color: 'white',
              },
            },
            '.slick-prev, .slick-next': {
              zIndex: 1,
              width: 40,
              height: 40,
              '&:before': {
                fontSize: '40px',
              },
            },
            '.slick-prev': {
              left: { xs: '10px', md: '-50px' },
            },
            '.slick-next': {
              right: { xs: '10px', md: '-50px' },
            },
            maxWidth: { xs: '400px', sm: '500px', md: '600px' },
            margin: '0 auto',
          }}>
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={4000}
              pauseOnHover={true}
              arrows={true}
              fade={true}
            >
              {testimonialImages.map((image, index) => (
                <Box key={index} sx={{ px: 2 }}>
                  <Box
                    component="img"
                    src={image}
                    alt={`Testimonial ${index + 1}`}
                    sx={{
                      width: '100%',
                      aspectRatio: '1/1',
                      objectFit: 'contain',
                      borderRadius: 2,
                      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                      display: 'block',
                      margin: '0 auto',
                      bgcolor: 'background.paper',
                    }}
                  />
                </Box>
              ))}
            </Slider>
          </Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.light', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            component="h2" 
            textAlign="center" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mb: { xs: 4, md: 8 },
            }}
          >
            Our Services
          </Typography>
          <Grid container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', border: 'none', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="240"
                  image="/images/services/airport-transfer.jpg"
                  alt="Airport Transfer"
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Airport Transfers
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    First-class airport transfers with our luxury V-Class vehicles. Includes flight monitoring and meet & greet service.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/booking"
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 'auto',
                      bgcolor: 'primary.main',
                      color: 'common.white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', border: 'none', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="240"
                  image="/images/services/corporate.jpg"
                  alt="Corporate Travel"
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    City to City & Corporate Travel
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Premium intercity transportation and corporate travel solutions. Spacious comfort for business journeys and city transfers.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/booking"
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 'auto',
                      bgcolor: 'primary.main',
                      color: 'common.white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', border: 'none', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="240"
                  image="/images/services/special-occasions.jpg"
                  alt="Special Events"
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Special Events
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Make your special occasions truly memorable with our luxury V-Class chauffeur service. Perfect for weddings, celebrations, and VIP events.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/booking"
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 'auto',
                      bgcolor: 'primary.main',
                      color: 'common.white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 